import type { ToggleDefinitionInput } from './ToggleDefinitionModal'
import type { CreateFeatureToggleInput } from 'app/generated/gql/graphql'
import { useEffect, useMemo, useState } from 'react'
import { FEATURE_TOGGLE_NAMES } from 'app/generated/featureToggleNames'
import { FeatureToggleJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useFeatureToggleStore, useFeatureToggleDefinitionStore } from 'app/stores/StoreContext'
import { useCustomerUserIds } from 'app/hooks/useCustomerUserIds'
import type { ReleaseGroups } from 'app/types/feature-toggle-release'
import { groupFeatureRowsByRelease, releaseTogglesToGive } from 'lib/featureToggleReleaseGroups'
import { categoryFromWire, categoryToWire, getToggleMetadata } from './featureToggleMetadata'
import { FEATURE_TOGGLE_RELEASES } from './featureToggleReleases'
import {
  buildFeatureRows,
  buildTargetRows,
  groupFeatureRowsByCategory,
  type FeatureRow,
  type TargetRef,
  type TargetRow,
} from './featureToggleModel'

export type ToggleView = `feature` | `target` | `release`

type CreateToggleInput = { name: string; type: TargetRef[`kind`] | `Global`; typeId?: string }

export type UseFeatureTogglesReturn = {
  loading: boolean
  view: ToggleView
  setView: (v: ToggleView) => void
  search: string
  setSearch: (s: string) => void
  featureGroups: Array<{ category: string; rows: FeatureRow[] }>
  releaseGroups: ReleaseGroups
  onToggleGlobal: (row: FeatureRow, next: boolean) => void | Promise<void>
  onSetOff: (row: FeatureRow) => void | Promise<void>
  onSetSpecific: (row: FeatureRow) => void | Promise<void>
  onRemoveOverride: (record: FeatureToggleJson) => void | Promise<void>
  target: TargetRef | null
  setTarget: (t: TargetRef | null) => void
  targetRows: TargetRow[]
  onToggleTarget: (row: TargetRow, next: boolean) => void | Promise<void>
  pendingGlobal: FeatureRow | null
  confirmGlobal: (deleteOverrides: boolean) => void
  cancelGlobal: () => void
  pendingOff: FeatureRow | null
  confirmOff: () => void
  cancelOff: () => void
  saveDefinition: (input: ToggleDefinitionInput, id?: string) => Promise<boolean>
  onAddOverrides: (name: string, kind: TargetRef[`kind`], ids: string[]) => Promise<void>
  definitionIdFor: (name: string) => string | undefined
  onSetArchived: (row: FeatureRow, archived: boolean) => Promise<void>
  releaseOf: (name: string) => string | null
  onGiveRelease: (rows: FeatureRow[], userIds: string[]) => Promise<void>
}

export function useFeatureToggles(): UseFeatureTogglesReturn {
  const store = useFeatureToggleStore()
  const defStore = useFeatureToggleDefinitionStore()
  const [view, setView] = useState<ToggleView>(`feature`)
  const [search, setSearch] = useState(``)
  const [target, setTarget] = useState<TargetRef | null>(null)
  const [pendingGlobal, setPendingGlobal] = useState<FeatureRow | null>(null)
  const [pendingOff, setPendingOff] = useState<FeatureRow | null>(null)

  useEffect(() => {
    bg(async () => {
      await store.fetchAll()
    })
  }, [])

  useEffect(() => {
    bg(async () => {
      await defStore.loadAll()
    })
  }, [])

  const records = store.hits as unknown as FeatureToggleJson[]

  const rows = useMemo(
    () => buildFeatureRows(records, FEATURE_TOGGLE_NAMES, defStore.metaByName, defStore.archivedNames),
    [records, defStore.metaByName, defStore.archivedNames],
  )

  const featureGroups = useMemo(() => {
    const filtered = search.trim()
      ? rows.filter((r) => {
          const q = search.toLowerCase()
          return (
            r.name.toLowerCase().includes(q) ||
            r.label.toLowerCase().includes(q) ||
            (r.meta?.category ?? ``).toLowerCase().includes(q)
          )
        })
      : rows
    return groupFeatureRowsByCategory(filtered)
  }, [rows, search])

  const customerUserIds = useCustomerUserIds(
    rows.flatMap((r) => r.userOverrides.map((o) => o.typeId)).filter((id): id is string => !!id),
  )

  // The API cannot tell a cleared release from one never set, so the code
  // metadata's release applies whenever none is stored. Read it from the code
  // metadata, never `row.meta`: a database definition replaces that on the row.
  const releaseOf = (name: string) => defStore.releaseByName.get(name) ?? getToggleMetadata(name)?.release ?? null

  const releaseGroups = useMemo(
    () => groupFeatureRowsByRelease(rows, FEATURE_TOGGLE_RELEASES, releaseOf, customerUserIds),
    [rows, customerUserIds, defStore.releaseByName],
  )

  const targetRows = useMemo(
    () =>
      target ? buildTargetRows(records, FEATURE_TOGGLE_NAMES, target, defStore.metaByName, defStore.archivedNames) : [],
    [records, target, defStore.metaByName, defStore.archivedNames],
  )

  const createRow = async (input: CreateToggleInput) => {
    const res = await store.create(input)
    if (res.error) notifyError(res.error.message)
    else notifySuccess(`Toggle updated`)
  }

  const removeRow = async (id: string) => {
    const res = await store.remove(id)
    if (res.error) notifyError(res.error.message)
    else notifySuccess(`Toggle updated`)
  }

  const onToggleGlobal = (row: FeatureRow, next: boolean): void | Promise<void> => {
    if (next) {
      const hasOverrides = row.workspaceOverrides.length > 0 || row.userOverrides.length > 0
      if (hasOverrides) {
        setPendingGlobal(row)
        return
      }
      return createRow({ name: row.name, type: `Global` })
    }
    if (row.global?.id) return removeRow(row.global.id)
  }

  const confirmGlobal = (deleteOverrides: boolean) => {
    const row = pendingGlobal
    setPendingGlobal(null)
    if (!row) return
    bg(async () => {
      if (deleteOverrides) {
        for (const o of [...row.workspaceOverrides, ...row.userOverrides]) {
          if (!o.id) continue
          const res = await store.remove(o.id)
          if (res.error) {
            notifyError(res.error.message)
            // Some removes may have landed before the failure — re-sync so the
            // UI reflects actual server state rather than a partial local view.
            await store.fetchAll()
            return
          }
        }
      }
      const res = await store.create({ name: row.name, type: `Global` })
      if (res.error) notifyError(res.error.message)
      else notifySuccess(`Toggle updated`)
    })
  }

  const cancelGlobal = () => setPendingGlobal(null)

  // Turn the toggle off for everyone: drop the global row and every assignment.
  // Confirm first when assignments would be removed.
  const onSetOff = (row: FeatureRow): void | Promise<void> => {
    const hasOverrides = row.workspaceOverrides.length > 0 || row.userOverrides.length > 0
    if (hasOverrides) {
      setPendingOff(row)
      return
    }
    if (row.global?.id) return removeRow(row.global.id)
  }

  const confirmOff = () => {
    const row = pendingOff
    setPendingOff(null)
    if (!row) return
    bg(async () => {
      const toRemove = [row.global, ...row.workspaceOverrides, ...row.userOverrides]
      for (const rec of toRemove) {
        if (!rec?.id) continue
        const res = await store.remove(rec.id)
        if (res.error) {
          notifyError(res.error.message)
          // Re-sync after a partial failure so the UI matches server state.
          await store.fetchAll()
          return
        }
      }
      notifySuccess(`Turned off`)
    })
  }

  const cancelOff = () => setPendingOff(null)

  // Switch to "specific": no longer on for everyone. Drop the global row; any
  // existing per-target assignments stay, and the row expands to manage them.
  const onSetSpecific = (row: FeatureRow): void | Promise<void> => {
    if (row.global?.id) return removeRow(row.global.id)
  }

  // Metadata (displayName/category/description) now lives on FeatureToggle rows;
  // the API syncs it across all rows sharing a name. Editing updates any existing
  // row; creating a brand-new toggle writes a non-enabling "anchor" row (type
  // `None`) so the toggle stays in the catalog while Off. Category is sent as its
  // UPPERCASE wire enum value.
  const saveDefinition = async (input: ToggleDefinitionInput, id?: string) => {
    const payload: Omit<CreateFeatureToggleInput, 'name' | 'type'> = {
      displayName: input.displayName,
      description: input.description,
    }
    const category = categoryFromWire(input.category)
    if (!category || (!id && !input.name)) {
      notifyError(`Name and category are required`)
      return false
    }
    payload.category = categoryToWire(category)
    if (input.release !== undefined) payload.release = input.release
    const res = id
      ? await store.update(id, payload)
      : await store.create({
          name: input.name!,
          type: `None`,
          displayName: payload.displayName,
          description: payload.description,
          category: payload.category,
          release: payload.release,
        })
    if (res.error) {
      notifyError(res.error.message)
      return false
    }
    notifySuccess(id ? `Toggle updated` : `Toggle created`)
    await Promise.all([store.fetchAll(), defStore.loadAll()])
    return true
  }

  const onAddOverrides = async (name: string, kind: TargetRef[`kind`], ids: string[]) => {
    for (const id of ids) {
      const res = await store.create({ name, type: kind, typeId: id })
      if (res.error) {
        notifyError(res.error.message)
        return
      }
    }
    notifySuccess(`Overrides added`)
  }

  // Any row of a name can be updated — the API syncs metadata to its siblings —
  // so prefer a real row id from the loaded toggles, falling back to the deduped
  // definition. Undefined => no row yet => saveDefinition creates the anchor.
  const definitionIdFor = (name: string) => records.find((r) => r.name === name)?.id ?? defStore.byName.get(name)?.id

  // The archived flag lives on every record of a name and the API syncs it, so
  // any record will do. A toggle with no record yet gets a `None` anchor first;
  // create takes no archived input.
  const onSetArchived = async (row: FeatureRow, archived: boolean) => {
    let id = definitionIdFor(row.name)
    if (!id) {
      const created = await store.create({ name: row.name, type: `None` })
      if (created.error) {
        notifyError(created.error.message)
        return
      }
      id = created.data?.createFeatureToggle.id
    }
    if (!id) return
    const res = await store.update(id, { archived })
    if (res.error) {
      notifyError(res.error.message)
      return
    }
    notifySuccess(archived ? `Toggle archived` : `Toggle unarchived`)
    // archivedNames reads the definitions; refetching the records too would
    // flip the page to its loading state and lose the scroll position.
    await defStore.loadAll()
  }

  const onGiveRelease = async (releaseRows: FeatureRow[], userIds: string[]) => {
    const gifts = userIds.flatMap((typeId) =>
      releaseTogglesToGive(releaseRows, typeId).map((name) => ({ name, typeId })),
    )
    for (const { name, typeId } of gifts) {
      const res = await store.create({ name, type: `User`, typeId })
      if (res.error) {
        notifyError(res.error.message)
        return
      }
    }
    notifySuccess(
      gifts.length ? `Turned on ${gifts.length} toggle${gifts.length === 1 ? `` : `s`}` : `Already on for them`,
    )
  }

  const onRemoveOverride = (record: FeatureToggleJson) => {
    if (record.id) return removeRow(record.id)
  }

  const onToggleTarget = (row: TargetRow, next: boolean): void | Promise<void> => {
    if (!target) return
    if (next) return createRow({ name: row.name, type: target.kind, typeId: target.id })
    if (row.state.kind === `override` && row.state.record.id) return removeRow(row.state.record.id)
  }

  return {
    loading: store.listLoading,
    view,
    setView,
    search,
    setSearch,
    featureGroups,
    releaseGroups,
    onToggleGlobal,
    onSetOff,
    onSetSpecific,
    onRemoveOverride,
    target,
    setTarget,
    targetRows,
    onToggleTarget,
    pendingGlobal,
    confirmGlobal,
    cancelGlobal,
    pendingOff,
    confirmOff,
    cancelOff,
    saveDefinition,
    onAddOverrides,
    definitionIdFor,
    onSetArchived,
    releaseOf,
    onGiveRelease,
  }
}
